import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { PROMPT_DEFINITIONS } from '../data/aiPrompts';
import { buildMessagesFromPrompt, mergePrompts } from '../utils/promptHelpers';

// Use canonical prompt definitions as fallback when Firestore is empty
const FALLBACK_PROMPTS = PROMPT_DEFINITIONS;

const PromptContext = createContext(null);

export const usePrompts = () => {
  const context = useContext(PromptContext);
  if (!context) {
    throw new Error('usePrompts must be used within a PromptProvider');
  }
  return context;
};

export const PromptProvider = ({ children }) => {
  const [prompts, setPrompts] = useState(FALLBACK_PROMPTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Cache TTL in milliseconds (5 minutes)
  const CACHE_TTL = 5 * 60 * 1000;

  // Load prompts from Firestore
  const loadPrompts = useCallback(async (force = false) => {
    // Skip if cache is still valid and not forced
    if (!force && lastFetch && Date.now() - lastFetch < CACHE_TTL) {
      return;
    }

    try {
      const snapshot = await getDocs(collection(db, 'ai_prompts'));

      if (snapshot.empty) {
        // Use fallbacks if no prompts in Firestore
        console.log('No prompts in Firestore, using fallbacks');
        setPrompts(FALLBACK_PROMPTS);
      } else {
        const firestoreDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPrompts(mergePrompts(FALLBACK_PROMPTS, firestoreDocs));
      }

      setLastFetch(Date.now());
      setError(null);
    } catch (err) {
      console.error('Error loading prompts from Firestore:', err);
      setError(err.message);
      // Keep using fallbacks on error
    } finally {
      setLoading(false);
    }
  }, [lastFetch]);

  // Initial load
  useEffect(() => {
    loadPrompts();
  }, []);

  // Get a specific prompt by ID
  const getPrompt = useCallback((promptId) => {
    return prompts[promptId] || FALLBACK_PROMPTS[promptId] || null;
  }, [prompts]);

  // Build messages from a prompt template
  const buildMessages = useCallback((promptId, variables = {}) => {
    const prompt = getPrompt(promptId);
    if (!prompt) {
      throw new Error(`Prompt not found: ${promptId}`);
    }
    return buildMessagesFromPrompt(prompt, variables);
  }, [getPrompt]);

  // Log prompt usage
  const logUsage = useCallback(async (promptId, data) => {
    try {
      const prompt = getPrompt(promptId);
      await addDoc(collection(db, 'prompt_usage_logs'), {
        promptId,
        promptVersion: prompt?.currentVersion || 1,
        product: prompt?.product || 'unknown',
        ...data,
        calledAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error logging prompt usage:', err);
      // Don't throw - logging failure shouldn't break the app
    }
  }, [getPrompt]);

  // Submit feedback
  const submitFeedback = useCallback(async (promptId, feedbackData) => {
    try {
      const prompt = getPrompt(promptId);
      await addDoc(collection(db, 'prompt_feedback'), {
        promptId,
        promptVersion: prompt?.currentVersion || 1,
        product: prompt?.product || 'unknown',
        ...feedbackData,
        ratedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error submitting feedback:', err);
      throw err;
    }
  }, [getPrompt]);

  const value = {
    prompts,
    loading,
    error,
    getPrompt,
    buildMessages,
    logUsage,
    submitFeedback,
    refreshPrompts: () => loadPrompts(true),
  };

  return (
    <PromptContext.Provider value={value}>
      {children}
    </PromptContext.Provider>
  );
};

export default PromptContext;
